import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../../lib/admin";
import { CarPhoto } from "../CarCard";

const BATCH = 3;

export const PhotoUploader = ({ photos, setPhotos }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(null);
  const [drag, setDrag] = useState(false);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let done = 0;
    setBusy({ done, total: files.length });
    for (let i = 0; i < files.length; i += BATCH) {
      const fd = new FormData();
      files.slice(i, i + BATCH).forEach((f) => fd.append("files", f));
      try {
        const { data } = await adminApi.post("/upload", fd);
        setPhotos((p) => [...p, ...(data?.paths ?? [])]);
      } catch (e) { toast.error(errMsg(e)); }
      done = Math.min(files.length, i + BATCH);
      setBusy({ done, total: files.length });
    }
    setBusy(null);
    if (input.current) input.current.value = "";
  };
  const move = (i, d) => setPhotos((p) => { const a = [...p]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const cover = (i) => setPhotos((p) => [p[i], ...p.filter((_, k) => k !== i)]);

  return (
    <div data-testid="photo-uploader">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => !busy && input.current?.click()} data-testid="photo-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-[var(--red)] bg-red-50" : "border-[var(--line)] hover:border-[var(--ink)] bg-[var(--bone)]"}`}>
        {busy ? (
          <p className="inline-flex items-center gap-2 text-sm font-semibold"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento {busy.done}/{busy.total}…</p>
        ) : (
          <>
            <ImagePlus className="w-8 h-8 mx-auto text-[var(--muted)]" />
            <p className="mt-2 text-sm font-semibold">Trascina qui le foto o clicca per selezionarle</p>
            <p className="text-xs text-[var(--muted)]">Tutte le foto che vuoi, qualsiasi formato (JPG, PNG, HEIC, WEBP…)</p>
          </>
        )}
        <input ref={input} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
      </div>
      {photos.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photos.map((p, i) => (
            <div key={p} className="relative group aspect-[4/3] rounded-xl overflow-hidden bg-[#E9E6DE]" data-testid={`photo-item-${i}`}>
              <CarPhoto path={p} alt="" />
              {i === 0 && <span className="absolute top-1.5 left-1.5 rounded-full bg-[var(--red)] text-white text-[10px] font-bold px-2 py-0.5">Copertina</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between p-1.5 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} className="p-1 rounded bg-white/90" aria-label="Sposta a sinistra" data-testid={`photo-left-${i}`}><ChevronLeft className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="p-1 rounded bg-white/90" aria-label="Sposta a destra" data-testid={`photo-right-${i}`}><ChevronRight className="w-3.5 h-3.5" /></button>
                  {i > 0 && <button type="button" onClick={() => cover(i)} className="p-1 rounded bg-white/90" aria-label="Imposta copertina" data-testid={`photo-cover-${i}`}><Star className="w-3.5 h-3.5" /></button>}
                </div>
                <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x !== p))} className="p-1 rounded bg-[var(--red)] text-white" aria-label="Rimuovi" data-testid={`photo-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
